import { Route } from "nextjs-routes";

import {
  adminSettingsNavItem,
  getVisiblePrimaryItems,
  getVisibleSecondaryItems,
  type AdminNavVisibilityContext,
  type AdminPrimaryNavItem,
} from "@src/features/app/left-nav/admin/adminNavConfig";
import { RouteItem } from "./useAllJumpToItems";

const hrefToRoute = (href: string): Route => {
  const [pathname, queryString] = href.split("?");
  if (!queryString) {
    return { pathname } as Route;
  }
  const query: Record<string, string> = {};
  new URLSearchParams(queryString).forEach((value, key) => {
    query[key] = value;
  });
  return { pathname, query } as Route;
};

const secondaryItemId = (primaryId: string, label: string) =>
  `${primaryId}-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;

const countSecondaryLabels = (
  primaries: AdminPrimaryNavItem[],
  context: AdminNavVisibilityContext,
): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const primary of primaries) {
    for (const secondary of getVisibleSecondaryItems(primary.items, context)) {
      counts[secondary.label] = (counts[secondary.label] ?? 0) + 1;
    }
  }
  return counts;
};

export const buildJumpToRouteItemsFromAdminNav = (
  context: AdminNavVisibilityContext,
): RouteItem[] => {
  const items: RouteItem[] = [];
  const seenIds = new Set<string>();

  const addItem = (item: RouteItem) => {
    if (seenIds.has(item.id.toString())) {
      return;
    }
    seenIds.add(item.id.toString());
    items.push(item);
  };

  const visiblePrimaries = getVisiblePrimaryItems(context);
  const labelCounts = countSecondaryLabels(
    [...visiblePrimaries, adminSettingsNavItem],
    context,
  );
  const secondaryName = (primaryLabel: string, label: string) =>
    labelCounts[label] > 1 ? `${primaryLabel} ${label.toLowerCase()}` : label;

  for (const primary of visiblePrimaries) {
    if (primary.href) {
      addItem({
        id: primary.id,
        name: primary.label,
        route: hrefToRoute(primary.href),
        type: "route",
        icon: primary.Icon,
      });
    }
    for (const secondary of getVisibleSecondaryItems(primary.items, context)) {
      if (!secondary.href || secondary.disabled || secondary.hideFromJumpTo) {
        continue;
      }
      addItem({
        id: secondaryItemId(primary.id, secondary.label),
        name: secondaryName(primary.label, secondary.label),
        route: hrefToRoute(secondary.href),
        type: "route",
        icon: secondary.Icon,
      });
    }
  }

  if (context.permissions?.changeOrgSettings && adminSettingsNavItem.href) {
    addItem({
      id: adminSettingsNavItem.id,
      name: adminSettingsNavItem.label,
      route: hrefToRoute(adminSettingsNavItem.href),
      type: "route",
      icon: adminSettingsNavItem.Icon,
    });
    for (const secondary of getVisibleSecondaryItems(
      adminSettingsNavItem.items,
      context,
    )) {
      if (!secondary.href || secondary.disabled || secondary.hideFromJumpTo) {
        continue;
      }
      addItem({
        id: secondaryItemId(adminSettingsNavItem.id, secondary.label),
        name: secondaryName(adminSettingsNavItem.label, secondary.label),
        route: hrefToRoute(secondary.href),
        type: "route",
        icon: secondary.Icon,
      });
    }
  }

  return items;
};
